import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import { DEFAULT_HOUSE_COLOR } from '../../components/common/HouseBars';
import './HouseBoardScreen.css';

/* The house standing for a wall — a lobby display or a beamer — reached by a PUBLIC address.

   ★★ No login. The token in the address is the key, minted on /houses and revocable there, so a
   display can be left on all week without anybody's session on it. The payload is the standing
   only — houses, colours, points — never a person.

   ★ Same object as the event's ranking screen (CompanyEventRankingScreen): no nav, everything in
   `vh`, refreshes itself, F for fullscreen. A company has a handful of houses, so it never pages:
   the rows share the height of the screen instead.

   ⚠️ The bar is relative to the LEADER, like HouseBars: a scale to a round number would make the
   house in front look like it was losing to something off-screen. */

const REFRESH_MS = 30000;

const cssUrl = (image) => (
  typeof image === 'string' && image.includes(' ') ? encodeURI(image) : image);

const HouseBoardScreen = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [gone, setGone] = useState(false);

  const load = useCallback(() => {
    apiRequest('GET', `/public/house-board/${token}`)
      .then((res) => { setData(res); setGone(false); })
      // ★ A failed refresh keeps the last standing on the wall (`data` is left alone); only a
      // board that never loaded says it is gone. A display that blanks on one dropped request
      // looks broken from the back of the room.
      .catch(() => setGone(true));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const toggleFull = useCallback(() => {
    if (document.fullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen();
    } else if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'f' || e.key === 'F') toggleFull(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleFull]);

  const houses = (data && data.houses) || [];
  const top = houses.reduce((best, h) => Math.max(best, Number(h.points) || 0), 0);
  const company = (data && data.company) || {};

  return (
    <div className="hb">
      <div className="hb__head">
        {company.logo && (
          <span className="hb__logo" style={{ backgroundImage: `url("${cssUrl(company.logo)}")` }} />
        )}
        <span className="hb__title">
          {company.name && <span className="hb__company">{company.name}</span>}
          {t('houseBoardTitle')}
        </span>
      </div>

      <div className="hb__list">
        {gone && !data && <p className="hb__note">{t('houseBoardGone')}</p>}
        {!gone && !data && <p className="hb__note">{t('pleaseWait')}</p>}
        {data && houses.length === 0 && <p className="hb__note">{t('houseBoardEmpty')}</p>}
        {houses.map((h, i) => {
          const points = Number(h.points) || 0;
          const pct = top > 0 ? Math.max(0, Math.min(100, (points / top) * 100)) : 0;
          const color = h.color || DEFAULT_HOUSE_COLOR;
          const rank = h.rank || i + 1;
          return (
            <div key={h.id || i} className={`hb-row${rank === 1 && points > 0 ? ' hb-row--lead' : ''}`}>
              <span className="hb-row__rank">{rank}</span>
              <span
                className="hb-row__swatch"
                style={{ backgroundColor: color,
                         backgroundImage: h.image ? `url("${cssUrl(h.image)}")` : undefined }}
              />
              <span className="hb-row__body">
                <span className="hb-row__top">
                  <span className="hb-row__name">{h.name}</span>
                  <span className="hb-row__points">
                    {Math.round(points).toLocaleString()}
                    <span className="hb-row__pts">{t('pts')}</span>
                  </span>
                </span>
                <span className="hb-row__track">
                  <span className="hb-row__fill"
                        style={{ width: `${Math.max(pct, 1)}%`, backgroundColor: color }} />
                </span>
              </span>
            </div>
          );
        })}
      </div>

      <div className="hb__bar">
        <button type="button" className="hb__btn" onClick={toggleFull}
                title={t('rankingScreenFullscreen')}>
          <Icon name="expand" theme="light" />
        </button>
      </div>
    </div>
  );
};

export default HouseBoardScreen;
